import {useState, useEffect, useRef, useMemo} from 'react'
import {Button, Form, Modal, Spinner, Badge, ListGroup} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import Select from 'react-select'
import {useMutation, useQuery, useLazyQuery} from '@apollo/client'
import {toast} from 'react-toastify'
import {useDebounce} from '../../../../../_metronic/helpers'
import {CREATE_PDF_ENVELOPE} from '../../../gql/mutations/pdfEnvelopesMutations'
import {GET_ALL_PDF_DOCUMENT_TEMPLATES_ACTIVE, SEARCH_CONTACTS_FOR_SIGNER} from '../../../gql/queries/pdfEnvelopesApiQueries'
import {GET_ALL_USERS_FILTER, GET_ALL_USERS_BASIC} from '../../../gql/queries/usersQuery'
import {NodePdfDocumentTemplate, PdfFieldDefinition, PdfSignerRole, SignerFormInput, SigningLinkResult} from '../../../types/pdfEnvelopesApi'
import {STORAGE_NAMES, BACKEND_URL} from '../../../helpers/consts'
import {parseId} from '../../../helpers'
import {useGetUser} from '../../../store/userStore'
import {PdfWithFieldOverlay} from './PdfWithFieldOverlay'

interface Props {
  isOpen: boolean
  onClose: () => void
  preSelectedTemplateId?: string
  sourceType?: string
  sourceId?: string
  preAgentId?: string
  refetch?: () => void
}

interface ContactResult {
  id: string
  fullName: string
  email?: string | null
  phone?: string | null
}

interface AgentNode {
  id: string
  firstName: string
  lastName: string
  email: string
  npn?: string | null
  cellphone?: string | null
  primaryGroup?: {id: string; name: string} | null
  organization?: {id: string; name: string} | null
}

type AgentOption = {label: string; value: string; node: AgentNode}

const ROLE_COLORS = ['#2980b9', '#27ae60', '#e74c3c', '#f39c12', '#8e44ad', '#16a085']
const requiredMark = '*'

const EMPTY_SIGNER = (role: string, index: number): SignerFormInput => ({
  role,
  recipientName: '',
  recipientEmail: '',
  recipientPhone: '',
  signingOrderIndex: index,
})

function parseJsonArray(raw: any): any[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) } catch { return [] }
  }
  return []
}

function buildAgentOption(node: AgentNode): AgentOption {
  return {
    label: `${node.firstName} ${node.lastName} - ${node.email}${node.npn ? ` (${node.npn})` : ''}`,
    value: node.id,
    node,
  }
}

// Derive text field values from template fields, agent node and signers for live preview
function buildFieldValues(
  fields: PdfFieldDefinition[],
  agentNode: AgentNode | null,
  signers: SignerFormInput[],
  textOverrides: Record<string, string>
): Record<string, string> {
  const result: Record<string, string> = {}
  for (const field of fields) {
    if (field.type.startsWith('agent_') && agentNode) {
      switch (field.type) {
        case 'agent_name': result[field.id] = `${agentNode.firstName} ${agentNode.lastName}`; break
        case 'agent_npn': result[field.id] = agentNode.npn ?? ''; break
        case 'agent_email': result[field.id] = agentNode.email ?? ''; break
        case 'agent_phone': result[field.id] = agentNode.cellphone ?? ''; break
        case 'agent_organization': result[field.id] = agentNode.organization?.name ?? ''; break
      }
    } else if (field.type === 'text') {
      const val = textOverrides[field.id]
      if (val !== undefined) result[field.id] = val
      else if (field.defaultValue) result[field.id] = field.defaultValue
    } else {
      const signer = signers.find((s) => s.role === field.signerRole)
      if (signer) {
        switch (field.type) {
          case 'full_name': result[field.id] = signer.recipientName; break
          case 'first_name': result[field.id] = signer.recipientName.split(' ')[0] ?? ''; break
          case 'last_name': result[field.id] = signer.recipientName.split(' ').slice(1).join(' '); break
          case 'email': result[field.id] = signer.recipientEmail; break
          case 'phone': result[field.id] = signer.recipientPhone; break
          case 'id_number': result[field.id] = field.defaultValue ?? ''; break
          case 'date_of_birth': result[field.id] = field.defaultValue ?? ''; break
          case 'date_signed': result[field.id] = field.dateFormat ? `📅 ${field.dateFormat}` : '📅'; break
        }
      }
    }
  }
  return result
}

export const ModalRequestEnvelope = ({
  isOpen,
  onClose,
  preSelectedTemplateId,
  sourceType,
  sourceId,
  preAgentId,
  refetch,
}: Props) => {
  const intl = useIntl()
  const authUser = useGetUser((s) => s.user)

  const [selectedTemplateId, setSelectedTemplateId] = useState(preSelectedTemplateId ?? '')
  const [selectedTemplate, setSelectedTemplate] = useState<NodePdfDocumentTemplate | null>(null)
  const [signingOrder, setSigningOrder] = useState<'SEQUENTIAL' | 'PARALLEL'>('SEQUENTIAL')
  const [signers, setSigners] = useState<SignerFormInput[]>([])
  const [sendEmail, setSendEmail] = useState(true)
  const [sendSms, setSendSms] = useState(false)
  const [getSigningLink, setGetSigningLink] = useState(false)
  const [signingLinks, setSigningLinks] = useState<SigningLinkResult[]>([])
  const [done, setDone] = useState(false)
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)

  // Text field override values (fieldId → value)
  const [textFieldValues, setTextFieldValues] = useState<Record<string, string>>({})

  // Agent selector state
  const [agentId, setAgentId] = useState<string | null>(preAgentId ?? null)
  const [selectedAgent, setSelectedAgent] = useState<AgentOption | null>(null)
  const [selectedAgentNode, setSelectedAgentNode] = useState<AgentNode | null>(null)
  const [agentInput, setAgentInput] = useState('')
  const [agentOptions, setAgentOptions] = useState<AgentOption[]>([])
  const agentDeb = useDebounce(agentInput, 500)

  const [searchAgents, {data: agentsData, loading: agentsLoading}] = useLazyQuery(
    GET_ALL_USERS_FILTER,
    {fetchPolicy: 'no-cache'}
  )
  const [fetchAgent, {data: agentPreData}] = useLazyQuery(GET_ALL_USERS_BASIC)
  const agentPreNode: AgentNode | undefined = agentPreData?.allUsers?.edges?.[0]?.node

  // Contact search state
  const [contactSearchText, setContactSearchText] = useState<Record<number, string>>({})
  const [openDropdownIdx, setOpenDropdownIdx] = useState<number | null>(null)
  const [contactResults, setContactResults] = useState<ContactResult[]>([])
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const {data: templatesData} = useQuery(GET_ALL_PDF_DOCUMENT_TEMPLATES_ACTIVE, {
    fetchPolicy: 'no-cache',
  })
  const templates: NodePdfDocumentTemplate[] =
    templatesData?.allPdfDocumentTemplates?.edges?.map((e: any) => e.node) ?? []

  const [createEnvelope, {loading}] = useMutation(CREATE_PDF_ENVELOPE)

  const [searchContacts] = useLazyQuery(SEARCH_CONTACTS_FOR_SIGNER, {
    onCompleted: (data) => {
      setContactResults(data?.allContacts?.edges?.map((e: any) => e.node) ?? [])
    },
  })

  // Agent: fetch pre-selected agent on open (logged-in user or preAgentId)
  useEffect(() => {
    const targetId = preAgentId ?? authUser?.user?.id
    if (!targetId) return
    setAgentId(targetId)
    fetchAgent({variables: {id: Number(parseId(targetId))}})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preAgentId, authUser?.user?.id])

  // Agent: trigger search to populate options after pre-selection
  useEffect(() => {
    if (!agentPreNode) return
    searchAgents({variables: {name: agentPreNode.firstName, requireNpn: true}})
  }, [searchAgents, agentPreNode])

  // Agent: auto-select pre-loaded agent in the react-select
  useEffect(() => {
    if (!agentPreNode) return
    const opt = buildAgentOption(agentPreNode)
    setSelectedAgent(opt)
    setSelectedAgentNode(agentPreNode)
    setAgentId(agentPreNode.id)
  }, [agentPreNode])

  // Agent: update options from search results
  useEffect(() => {
    if (!agentsData) return
    setAgentOptions(agentsData.allUsers.edges.map(({node}: any) => buildAgentOption(node as AgentNode)))
  }, [agentsData])

  // Agent: debounced search when user types
  useEffect(() => {
    if (!agentDeb) return
    searchAgents({variables: {name: agentDeb, requireNpn: true}})
  }, [searchAgents, agentDeb])

  // PDF preview
  useEffect(() => {
    let objectUrl: string | null = null
    if (!selectedTemplate?.pdfFileUrl) {
      setPdfBlobUrl(null)
      return
    }
    setPdfLoading(true)
    const rawUrl = `${BACKEND_URL}/pdf-envelopes/templates/${parseId(selectedTemplate.id)}/pdf/`
    const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
    fetch(rawUrl, {headers: token ? {Authorization: `Bearer ${token}`} : {}})
      .then((r) => r.blob())
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob)
        setPdfBlobUrl(objectUrl)
      })
      .catch(() => setPdfBlobUrl(null))
      .finally(() => setPdfLoading(false))
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate?.id])

  const applyTemplate = (tpl: NodePdfDocumentTemplate | null) => {
    setSelectedTemplate(tpl)
    setPdfBlobUrl(null)
    setContactSearchText({})
    setOpenDropdownIdx(null)
    setContactResults([])
    if (tpl) {
      const roles: PdfSignerRole[] = parseJsonArray(tpl.signerRoles) as PdfSignerRole[]
      const fieldDefs: PdfFieldDefinition[] = parseJsonArray(tpl.fieldDefinitions) as PdfFieldDefinition[]

      // Auto-fill signers from defaultValue of full_name, email, phone fields
      setSigners(roles.map((r, i) => {
        const roleFields = fieldDefs.filter((f) => f.signerRole === r.role)
        const nameDefault = roleFields.find((f) => f.type === 'full_name')?.defaultValue ?? ''
        const emailDefault = roleFields.find((f) => f.type === 'email')?.defaultValue ?? ''
        const phoneDefault = roleFields.find((f) => f.type === 'phone')?.defaultValue ?? ''
        return {
          ...EMPTY_SIGNER(r.role, i),
          recipientName: nameDefault,
          recipientEmail: emailDefault,
          recipientPhone: phoneDefault,
        }
      }))

      // Initialize text field overrides from defaultValues
      const textInit: Record<string, string> = {}
      fieldDefs.filter((f) => f.type === 'text' && f.defaultValue).forEach((f) => {
        textInit[f.id] = f.defaultValue!
      })
      setTextFieldValues(textInit)
    } else {
      setSigners([])
      setTextFieldValues({})
    }
  }

  useEffect(() => {
    if (preSelectedTemplateId && templates.length > 0) {
      const tpl = templates.find((t) => t.id === preSelectedTemplateId) ?? null
      applyTemplate(tpl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length, preSelectedTemplateId])

  const handleTemplateChange = (id: string) => {
    setSelectedTemplateId(id)
    applyTemplate(templates.find((t) => t.id === id) ?? null)
  }

  const updateSigner = (i: number, key: keyof SignerFormInput, val: string | number) => {
    setSigners((p) => p.map((s, idx) => (idx === i ? {...s, [key]: val} : s)))
  }

  const handleContactSearch = (signerIdx: number, text: string) => {
    setContactSearchText((prev) => ({...prev, [signerIdx]: text}))
    setOpenDropdownIdx(signerIdx)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    if (text.trim().length < 2) {
      setContactResults([])
      return
    }
    searchTimerRef.current = setTimeout(() => {
      searchContacts({variables: {name: text, first: 8}})
    }, 350)
  }

  const selectContact = (signerIdx: number, contact: ContactResult) => {
    setSigners((prev) =>
      prev.map((s, idx) =>
        idx === signerIdx
          ? {
              ...s,
              recipientName: contact.fullName,
              recipientEmail: contact.email ?? '',
              recipientPhone: contact.phone ?? '',
              contactId: contact.id,
            }
          : s
      )
    )
    setContactSearchText((prev) => ({...prev, [signerIdx]: ''}))
    setOpenDropdownIdx(null)
    setContactResults([])
  }

  const handleSubmit = async () => {
    if (!selectedTemplateId) {
      toast.error(
        intl.formatMessage({
          id: 'documents.pdfEnvelopes.request.toast.selectTemplate',
          defaultMessage: 'Select a template',
        })
      )
      return
    }
    if (!agentId) {
      toast.error(
        intl.formatMessage({
          id: 'documents.pdfEnvelopes.request.toast.selectAgent',
          defaultMessage: 'Select an agent',
        })
      )
      return
    }
    if (!sendEmail && !sendSms && !getSigningLink) {
      toast.error(
        intl.formatMessage({
          id: 'documents.pdfEnvelopes.request.toast.selectChannel',
          defaultMessage: 'Select at least one delivery channel',
        })
      )
      return
    }
    if (signers.find((s) => !s.recipientName.trim())) {
      toast.error(
        intl.formatMessage({
          id: 'documents.pdfEnvelopes.request.toast.completeNames',
          defaultMessage: 'Complete the name of all signers',
        })
      )
      return
    }
    try {
      const {data} = await createEnvelope({
        variables: {
          templateId: parseId(selectedTemplateId),
          title: selectedTemplate?.name ?? 'Document',
          signingOrder,
          sourceType: sourceType ?? null,
          sourceId: sourceId ?? null,
          agentId: parseId(agentId),
          signers: signers.map((s) => ({
            ...s,
            contactId: s.contactId ? parseId(s.contactId) : undefined,
          })),
          sendEmail,
          sendSms,
          getSigningLink,
          fieldValues: Object.keys(textFieldValues).length > 0 ? JSON.stringify(textFieldValues) : undefined,
        },
      })
      if (data?.pdfEnvelopeCreate?.ok) {
        toast.success(
          intl.formatMessage({
            id: 'documents.pdfEnvelopes.request.toast.sent',
            defaultMessage: 'Envelope sent successfully',
          })
        )
        setSigningLinks(data.pdfEnvelopeCreate.signingLinks ?? [])
        setDone(true)
        refetch?.()
      } else {
        toast.error(
          data?.pdfEnvelopeCreate?.errorMessage ||
            intl.formatMessage({
              id: 'documents.pdfEnvelopes.request.toast.sendError',
              defaultMessage: 'Error sending',
            })
        )
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const signerRoles: PdfSignerRole[] = parseJsonArray(selectedTemplate?.signerRoles) as PdfSignerRole[]
  const templateFields: PdfFieldDefinition[] = parseJsonArray(selectedTemplate?.fieldDefinitions) as PdfFieldDefinition[]
  const textFields = templateFields.filter((f) => f.type === 'text')

  const roleLabel = (role: string) => signerRoles.find((r) => r.role === role)?.label ?? role
  const roleColor = (role: string) => {
    const idx = signerRoles.findIndex((r) => r.role === role)
    return ROLE_COLORS[idx >= 0 ? idx % ROLE_COLORS.length : 0]
  }

  // Build live preview field values
  const fieldValues = useMemo(
    () => buildFieldValues(templateFields, selectedAgentNode, signers, textFieldValues),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(templateFields), selectedAgentNode, JSON.stringify(signers), JSON.stringify(textFieldValues)]
  )

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size='xl'
      backdrop='static'
      keyboard={false}
      dialogClassName='modal-fullscreen-lg-down'
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage
            id='documents.pdfEnvelopes.request.title'
            defaultMessage='Send document for signature (PDF)'
          />
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{padding: 0, display: 'flex', height: 'calc(100vh - 160px)', overflow: 'hidden'}}>
        {done ? (
          <div className='p-4 w-100'>
            <div className='alert alert-success fw-bold'>
              <FormattedMessage
                id='documents.pdfEnvelopes.request.sentSuccess'
                defaultMessage='Document sent successfully.'
              />
            </div>
            {getSigningLink && signingLinks.length > 0 && (
              <>
                <p className='fw-bold mb-2'>
                  <FormattedMessage
                    id='documents.pdfEnvelopes.request.signingLinks'
                    defaultMessage='Signing links:'
                  />
                </p>
                {signingLinks.map((sl) => (
                  <div key={sl.role} className='mb-3 p-3 border rounded'>
                    <div className='d-flex align-items-center gap-2 mb-1'>
                      <span style={{
                        display: 'inline-block',
                        background: roleColor(sl.role),
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 4,
                      }}>
                        {roleLabel(sl.role)}
                      </span>
                    </div>
                    <a href={sl.link} target='_blank' rel='noreferrer' style={{wordBreak: 'break-all', fontSize: 13}}>
                      {sl.link}
                    </a>
                  </div>
                ))}
              </>
            )}
          </div>
        ) : (
          <>
            {/* Left — PDF preview with field overlays */}
            <div style={{flex: '1 1 60%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid #ddd'}}>
              {!selectedTemplate?.pdfFileUrl ? (
                <div className='d-flex flex-column align-items-center justify-content-center h-100 text-muted' style={{background: '#e9ecef'}}>
                  <i className='bi bi-file-earmark-pdf' style={{fontSize: 48, opacity: 0.3}} />
                  <p className='mt-2' style={{fontSize: 13}}>
                    <FormattedMessage
                      id='documents.pdfEnvelopes.request.previewHint'
                      defaultMessage='Select a template to preview the PDF'
                    />
                  </p>
                </div>
              ) : pdfLoading ? (
                <div className='d-flex align-items-center justify-content-center h-100' style={{background: '#e9ecef'}}>
                  <Spinner animation='border' className='me-2' />
                  <span className='text-muted'>
                    <FormattedMessage
                      id='documents.pdfEnvelopes.request.loadingPdf'
                      defaultMessage='Loading PDF...'
                    />
                  </span>
                </div>
              ) : pdfBlobUrl ? (
                <PdfWithFieldOverlay
                  pdfUrl={pdfBlobUrl}
                  fields={templateFields}
                  signerRoles={signerRoles}
                  fieldValues={fieldValues}
                />
              ) : (
                <div className='d-flex align-items-center justify-content-center h-100 text-muted' style={{background: '#e9ecef'}}>
                  <FormattedMessage
                    id='documents.pdfEnvelopes.request.loadError'
                    defaultMessage='Could not load PDF'
                  />
                </div>
              )}
            </div>

            {/* Right — form */}
            <div style={{width: 340, flexShrink: 0, overflowY: 'auto', padding: '20px 16px', background: '#fff'}}>

              {/* Agent selector */}
              <Form.Group className='mb-4'>
                <Form.Label className='fw-bold' style={{fontSize: 13}}>
                  <FormattedMessage
                    id='common.field.agent'
                    defaultMessage='Agent'
                  /> {requiredMark}
                </Form.Label>
                <Select
                  value={selectedAgent}
                  isLoading={agentsLoading}
                  placeholder={intl.formatMessage({
                    id: 'documents.pdfEnvelopes.request.agentPlaceholder',
                    defaultMessage: 'Search agent by name…',
                  })}
                  options={agentOptions}
                  inputValue={agentInput}
                  onInputChange={setAgentInput}
                  onChange={(opt) => {
                    const o = opt as AgentOption | null
                    setSelectedAgent(o)
                    setSelectedAgentNode(o?.node ?? null)
                    setAgentId(o?.value ?? null)
                  }}
                  isClearable
                  styles={{
                    control: (base) => ({...base, fontSize: 13, minHeight: 32}),
                    option: (base) => ({...base, fontSize: 13}),
                  }}
                />
                {/* Live agent data preview */}
                {selectedAgentNode && (
                  <div className='mt-1 p-2 rounded' style={{background: '#f0f4f8', fontSize: 11, color: '#555'}}>
                    {selectedAgentNode.npn && (
                      <div>
                        <strong>
                          <FormattedMessage
                            id='documents.pdfEnvelopes.request.agentInfo.npn'
                            defaultMessage='NPN:'
                          />
                        </strong> {selectedAgentNode.npn}
                      </div>
                    )}
                    {selectedAgentNode.cellphone && (
                      <div>
                        <strong>
                          <FormattedMessage
                            id='documents.pdfEnvelopes.request.agentInfo.phone'
                            defaultMessage='Phone:'
                          />
                        </strong> {selectedAgentNode.cellphone}
                      </div>
                    )}
                    {selectedAgentNode.organization?.name && (
                      <div>
                        <strong>
                          <FormattedMessage
                            id='documents.pdfEnvelopes.request.agentInfo.org'
                            defaultMessage='Org:'
                          />
                        </strong> {selectedAgentNode.organization.name}
                      </div>
                    )}
                  </div>
                )}
              </Form.Group>

              <Form.Group className='mb-4'>
                <Form.Label className='fw-bold' style={{fontSize: 13}}>
                  <FormattedMessage
                    id='documents.pdfEnvelopes.request.field.template'
                    defaultMessage='PDF template'
                  /> {requiredMark}
                </Form.Label>
                <Form.Select
                  size='sm'
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                >
                  <option value=''>
                    {intl.formatMessage({
                      id: 'common.placeholder.selectOption',
                      defaultMessage: 'Select an option',
                    })}
                  </option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              {signers.length > 1 && (
                <Form.Group className='mb-4'>
                  <Form.Label className='fw-bold' style={{fontSize: 13}}>
                    <FormattedMessage
                      id='documents.pdfEnvelopes.request.field.signingOrder'
                      defaultMessage='Signing order'
                    />
                  </Form.Label>
                  <div className='d-flex flex-column gap-1'>
                    {(['SEQUENTIAL', 'PARALLEL'] as const).map((o) => (
                      <Form.Check
                        key={o}
                        type='radio'
                        label={
                          o === 'SEQUENTIAL'
                            ? intl.formatMessage({
                                id: 'documents.pdfEnvelopes.request.order.sequential',
                                defaultMessage: 'Sequential (one at a time)',
                              })
                            : intl.formatMessage({
                                id: 'documents.pdfEnvelopes.request.order.parallel',
                                defaultMessage: 'Parallel (all at once)',
                              })
                        }
                        checked={signingOrder === o}
                        onChange={() => setSigningOrder(o)}
                        style={{fontSize: 13}}
                      />
                    ))}
                  </div>
                </Form.Group>
              )}

              {signers.length > 0 && (
                <div className='mb-4'>
                  <p className='fw-bold mb-2' style={{fontSize: 13}}>
                    <FormattedMessage
                      id='documents.pdfEnvelopes.request.signers'
                      defaultMessage='Signers'
                    />
                  </p>
                  {signers.map((s, i) => (
                    <div key={i} className='mb-3'>
                      <div className='d-flex align-items-center gap-2 mb-1'>
                        <div style={{
                          width: 12, height: 12, borderRadius: 3, flexShrink: 0,
                          background: roleColor(s.role),
                        }} />
                        <span className='fw-semibold' style={{fontSize: 13, color: roleColor(s.role)}}>
                          {roleLabel(s.role)}
                        </span>
                        {signingOrder === 'SEQUENTIAL' && (
                          <span className='text-muted ms-auto' style={{fontSize: 11}}>
                            {intl.formatMessage(
                              {
                                id: 'documents.pdfEnvelopes.request.signOrder',
                                defaultMessage: 'Sign #{n}',
                              },
                              {n: i + 1}
                            )}
                          </span>
                        )}
                      </div>

                      {/* Contact search */}
                      <div style={{position: 'relative', marginBottom: 4}}>
                        <Form.Control
                          size='sm'
                          placeholder={intl.formatMessage({
                            id: 'documents.pdfEnvelopes.request.contactPlaceholder',
                            defaultMessage: 'Search contact by name…',
                          })}
                          value={contactSearchText[i] ?? ''}
                          onChange={(e) => handleContactSearch(i, e.target.value)}
                          onBlur={() => setTimeout(() => setOpenDropdownIdx(null), 200)}
                          style={{fontSize: 12}}
                        />
                        {openDropdownIdx === i && contactResults.length > 0 && (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050,
                            background: '#fff', border: '1px solid #dee2e6', borderRadius: 4,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 180, overflowY: 'auto',
                          }}>
                            <ListGroup variant='flush'>
                              {contactResults.map((c) => (
                                <ListGroup.Item
                                  key={c.id}
                                  action
                                  onMouseDown={() => selectContact(i, c)}
                                  style={{fontSize: 12, padding: '5px 10px', cursor: 'pointer'}}
                                >
                                  <div className='fw-semibold'>{c.fullName}</div>
                                  {(c.email || c.phone) && (
                                    <div className='text-muted' style={{fontSize: 11}}>
                                      {[c.email, c.phone].filter(Boolean).join(' · ')}
                                    </div>
                                  )}
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          </div>
                        )}
                      </div>

                      <Form.Control
                        size='sm'
                        placeholder={intl.formatMessage({
                          id: 'documents.pdfEnvelopes.request.signerNamePlaceholder',
                          defaultMessage: 'Full name *',
                        })}
                        value={s.recipientName}
                        onChange={(e) => updateSigner(i, 'recipientName', e.target.value)}
                        className='mb-1'
                      />
                      <Form.Control
                        size='sm'
                        placeholder={intl.formatMessage({id: 'common.field.email', defaultMessage: 'Email'})}
                        type='email'
                        value={s.recipientEmail}
                        onChange={(e) => updateSigner(i, 'recipientEmail', e.target.value)}
                        className='mb-1'
                      />
                      <Form.Control
                        size='sm'
                        placeholder={intl.formatMessage({id: 'common.field.phone', defaultMessage: 'Phone'})}
                        value={s.recipientPhone}
                        onChange={(e) => updateSigner(i, 'recipientPhone', e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Text fields section */}
              {textFields.length > 0 && (
                <div className='mb-4'>
                  <p className='fw-bold mb-2' style={{fontSize: 13}}>
                    <FormattedMessage
                      id='documents.pdfEnvelopes.request.textFields'
                      defaultMessage='Text fields'
                    />
                  </p>
                  {textFields.map((f) => (
                    <Form.Group key={f.id} className='mb-2'>
                      <Form.Label style={{fontSize: 12, marginBottom: 2}}>
                        {f.label ||
                          intl.formatMessage({
                            id: 'documents.pdfEnvelopes.request.textFallback',
                            defaultMessage: 'Text',
                          })}
                      </Form.Label>
                      <Form.Control
                        size='sm'
                        value={textFieldValues[f.id] ?? f.defaultValue ?? ''}
                        onChange={(e) =>
                          setTextFieldValues((prev) => ({...prev, [f.id]: e.target.value}))
                        }
                        placeholder={
                          f.defaultValue
                            ? intl.formatMessage(
                                {
                                  id: 'documents.pdfEnvelopes.request.defaultValue',
                                  defaultMessage: 'Default: {value}',
                                },
                                {value: f.defaultValue}
                              )
                            : intl.formatMessage({
                                id: 'documents.pdfEnvelopes.request.enterValue',
                                defaultMessage: 'Enter value…',
                              })
                        }
                      />
                    </Form.Group>
                  ))}
                </div>
              )}

              <div className='mb-2'>
                <p className='fw-bold mb-2' style={{fontSize: 13}}>
                  <FormattedMessage
                    id='documents.pdfEnvelopes.request.deliveryChannels'
                    defaultMessage='Delivery channels'
                  />
                </p>
                <Form.Check
                  type='checkbox'
                  label={intl.formatMessage({id: 'common.field.email', defaultMessage: 'Email'})}
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className='mb-1'
                  style={{fontSize: 13}}
                />
                <Form.Check
                  type='checkbox'
                  label={intl.formatMessage({
                    id: 'documents.pdfEnvelopes.request.channel.sms',
                    defaultMessage: 'SMS',
                  })}
                  checked={sendSms}
                  onChange={(e) => setSendSms(e.target.checked)}
                  className='mb-1'
                  style={{fontSize: 13}}
                />
                <Form.Check
                  type='checkbox'
                  label={intl.formatMessage({
                    id: 'documents.pdfEnvelopes.request.channel.getLink',
                    defaultMessage: 'Get signing link',
                  })}
                  checked={getSigningLink}
                  onChange={(e) => setGetSigningLink(e.target.checked)}
                  style={{fontSize: 13}}
                />
              </div>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          {done ? (
            <FormattedMessage id='common.action.close' defaultMessage='Close' />
          ) : (
            <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
          )}
        </Button>
        {!done && (
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <Spinner size='sm' animation='border' />
            ) : (
              <FormattedMessage
                id='documents.pdfEnvelopes.request.send'
                defaultMessage='Send document'
              />
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
